import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  getBadgeConfigurations,
  getBadgeConfiguration,
  updateBadgeConfiguration,
  createBadgeConfiguration,
  getBadgeConfigurationHistory,
  getCentresFilter,
  getBadgeIconUrl,
  testTriggerBadgeAward
} from '../services/api';
import defaultBadgeIcon from '../assets/badges/default-badge-icon.svg';
import { getDateLocale } from '../i18n';

const BadgeConfigurationPage = () => {
  const { t } = useTranslation(['admin', 'common']);
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingBadge, setEditingBadge] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [centres, setCentres] = useState([]);
  const [loadingCentres, setLoadingCentres] = useState(false);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [testingBadge, setTestingBadge] = useState(null);
  const [iconFile, setIconFile] = useState(null);
  const [iconPreview, setIconPreview] = useState(null);
  const [currentIconUrl, setCurrentIconUrl] = useState(null);
  const [iconRemoved, setIconRemoved] = useState(false);
  const fileInputRef = useRef(null);
  const [minHoursHHMM, setMinHoursHHMM] = useState('');
  const [minHoursError, setMinHoursError] = useState('');
  const [centreSearchQuery, setCentreSearchQuery] = useState('');
  const [showTestResultsModal, setShowTestResultsModal] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const [applyingBadge, setApplyingBadge] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    points_value: 0,
    is_active: true,
    badge_type: 'DAILY',
    min_operations: 3,
    min_hours: null,
    tie_handling: 'no_badge',
    is_global: true,
    applicable_centre_ids: []
  });

  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];

  useEffect(() => {
    fetchBadges();
    fetchCentres();
  }, []);

  // Fetch centres when modal opens if not already loaded
  useEffect(() => {
    if (showEditModal && centres.length === 0 && !loadingCentres) {
      fetchCentres();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showEditModal]);

  // Ensure minHoursHHMM is set when editing badge
  useEffect(() => {
    if (editingBadge && showEditModal && formData.min_hours !== null && formData.min_hours !== undefined) {
      const hhmmValue = decimalHoursToHHMM(formData.min_hours);
      if (hhmmValue !== minHoursHHMM) {
        // #region agent log
        // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'BadgeConfigurationPage.js:useEffect',message:'Syncing minHoursHHMM from formData',data:{formDataMinHours:formData.min_hours,currentMinHoursHHMM:minHoursHHMM,newHhmmValue:hhmmValue},timestamp:Date.now(),sessionId:'debug-session',runId:'run2',hypothesisId:'F'})}).catch(()=>{});
        // #endregion
        setMinHoursHHMM(hhmmValue);
      }
    } else if (editingBadge && showEditModal && (formData.min_hours === null || formData.min_hours === undefined)) {
      if (minHoursHHMM !== '') {
        // #region agent log
        // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'BadgeConfigurationPage.js:useEffect',message:'Clearing minHoursHHMM (min_hours is null)',data:{formDataMinHours:formData.min_hours,currentMinHoursHHMM:minHoursHHMM},timestamp:Date.now(),sessionId:'debug-session',runId:'run2',hypothesisId:'F'})}).catch(()=>{});
        // #endregion
        setMinHoursHHMM('');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingBadge, showEditModal, formData.min_hours]);

  const fetchBadges = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getBadgeConfigurations();
      setBadges(response.results || response);
    } catch (err) {
      console.error('Failed to fetch badges:', err);
      setError(t('badges.loadConfigsFailed'));
    } finally {
      setLoading(false);
    }
  };

  const fetchCentres = async () => {
    setLoadingCentres(true);
    try {
      const response = await getCentresFilter();
      const centresArray = Array.isArray(response) ? response : [];
      setCentres(centresArray);
    } catch (err) {
      console.error('Failed to fetch centres:', err);
      setError(t('badges.loadCentresFailed'));
      setCentres([]);
    } finally {
      setLoadingCentres(false);
    }
  };

  // Filter centres based on search query
  const filteredCentres = centres.filter(c => {
    if (!c || !c.id) return false;
    if (!centreSearchQuery) return true;
    const searchLower = centreSearchQuery.toLowerCase();
    const name = (c.name || '').toLowerCase();
    return name.includes(searchLower);
  });

  const selectedCentresCount = formData.applicable_centre_ids.length;
  const totalCentresCount = filteredCentres.length;

  const handleSelectAllCentres = () => {
    const allCentreIds = filteredCentres.map(c => c.id).filter(id => id != null);
    setFormData({ ...formData, applicable_centre_ids: allCentreIds });
  };

  const handleDeselectAllCentres = () => {
    setFormData({ ...formData, applicable_centre_ids: [] });
  };

  const fetchHistory = async (badgeId) => {
    try {
      setLoadingHistory(true);
      const response = await getBadgeConfigurationHistory(badgeId);
      setHistory(response.results || response);
    } catch (err) {
      console.error('Failed to fetch history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleEdit = async (badge) => {
    try {
      const badgeData = await getBadgeConfiguration(badge.id);
      
      // #region agent log
      // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'BadgeConfigurationPage.js:handleEdit',message:'Full badge data from API',data:{badgeId:badge.id,badgeData:JSON.stringify(badgeData),hasMinHours:badgeData.hasOwnProperty('min_hours'),minHoursValue:badgeData.min_hours,minHoursType:typeof badgeData.min_hours},timestamp:Date.now(),sessionId:'debug-session',runId:'run2',hypothesisId:'A'})}).catch(()=>{});
      // #endregion
      
      setEditingBadge(badgeData);
      // Handle min_hours - convert to number if it's a string
      let minHours = badgeData.min_hours;
      if (minHours !== null && minHours !== undefined) {
        minHours = typeof minHours === 'string' ? parseFloat(minHours) : minHours;
        if (isNaN(minHours)) {
          minHours = null;
        }
      } else {
        minHours = null;
      }
      
      // #region agent log
      // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'BadgeConfigurationPage.js:handleEdit',message:'Badge data loaded for edit',data:{badgeId:badge.id,minHoursRaw:badgeData.min_hours,minHoursParsed:minHours,minHoursType:typeof badgeData.min_hours},timestamp:Date.now(),sessionId:'debug-session',runId:'run2',hypothesisId:'A'})}).catch(()=>{});
      // #endregion
      
      // Convert decimal hours to HH:MM for display BEFORE setting formData
      const hhmmValue = decimalHoursToHHMM(minHours);
      
      // #region agent log
      // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'BadgeConfigurationPage.js:handleEdit',message:'HH:MM conversion result',data:{minHours:minHours,hhmmValue:hhmmValue,minHoursType:typeof minHours},timestamp:Date.now(),sessionId:'debug-session',runId:'run2',hypothesisId:'B'})}).catch(()=>{});
      // #endregion
      
      // Set minHoursHHMM FIRST to ensure it's available when modal opens
      setMinHoursHHMM(hhmmValue);
      
      setFormData({
        name: badgeData.name || '',
        description: badgeData.description || '',
        points_value: badgeData.points_value || 0,
        is_active: badgeData.is_active !== false,
        badge_type: badgeData.badge_type || 'DAILY',
        min_operations: badgeData.min_operations || 3,
        min_hours: minHours,
        tie_handling: badgeData.tie_handling || 'no_badge',
        is_global: badgeData.is_global !== false,
        applicable_centre_ids: badgeData.applicable_centres?.map(c => c.id) || []
      });
      setMinHoursError('');
      setCentreSearchQuery('');
      // Set current icon URL if exists
      setCurrentIconUrl(badgeData.icon ? getBadgeIconUrl(badgeData.icon) : null);
      setIconFile(null);
      setIconPreview(null);
      setIconRemoved(false); // Reset removal flag when loading badge
      setShowEditModal(true);
      
      // #region agent log
      // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'BadgeConfigurationPage.js:handleEdit',message:'Modal opened, state set',data:{showEditModal:true,minHoursHHMM:hhmmValue},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'})}).catch(()=>{});
      // #endregion
    } catch (err) {
      console.error('Failed to fetch badge details:', err);
      setError(t('badges.loadDetailsFailed'));
    }
  };

  const handleViewHistory = async (badge) => {
    setEditingBadge(badge);
    await fetchHistory(badge.id);
    setShowHistoryModal(true);
  };

  const handleTestTrigger = async (badge) => {
    if (!badge.is_active) {
      setError(t('badges.mustBeActiveToTestError'));
      return;
    }

    setTestingBadge(badge.id);
    setError(null);
    try {
      // Test trigger without applying (evaluation only)
      const result = await testTriggerBadgeAward(badge.id, { apply: false });
      // Show modal with all evaluated employees
      setTestResults({
        badge: badge,
        result: result,
        allEvaluatedEmployees: result.all_evaluated_employees || [],
        maxOperationCount: result.max_operation_count || 0,
        awardedEmployeeIds: result.evaluation_only 
          ? (result.awarded_employee_ids || [])
          : (result.awarded_employees?.map(e => e.employee_id) || []),
        badgeCriteria: result.badge_criteria || {},
        evaluationOnly: result.evaluation_only || false,
        wouldAwardTo: result.would_award_to || 0
      });
      setShowTestResultsModal(true);
      
      if (result.success) {
        setSuccessMessage(result.message || t('badges.evaluationCompleted'));
        setTimeout(() => setSuccessMessage(null), 5000);
      } else {
        setError(result.message || t('badges.evaluateFailed'));
      }
    } catch (err) {
      console.error('Failed to trigger badge award:', err);
      
      // Extract detailed error message from API response
      let errorMessage = t('badges.triggerFailed');
      if (err.response?.data) {
        const errorData = err.response.data;
        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setTestingBadge(null);
    }
  };

  const handleApplyBadge = async () => {
    if (!testResults || !testResults.badge) {
      return;
    }

    setApplyingBadge(true);
    setError(null);
    try {
      // #region agent log
      // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'BadgeConfigurationPage.js:handleApplyBadge',message:'Before calling testTriggerBadgeAward with apply=true',data:{badgeId:testResults.badge.id,wouldAwardTo:testResults.wouldAwardTo},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
      // #endregion
      
      // Actually apply the badge award
      const result = await testTriggerBadgeAward(testResults.badge.id, { apply: true });
      
      // #region agent log
      // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'BadgeConfigurationPage.js:handleApplyBadge',message:'After calling testTriggerBadgeAward',data:{resultSuccess:result.success,resultMessage:result.message,awardedEmployeesCount:result.awarded_employees?.length || 0},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
      // #endregion
      
      // Update test results with actual award results
      setTestResults({
        ...testResults,
        result: result,
        awardedEmployeeIds: result.awarded_employees?.map(e => e.employee_id) || [],
        evaluationOnly: false
      });
      
      if (result.success) {
        setSuccessMessage(result.message || t('badges.awardSuccess'));
        setTimeout(() => setSuccessMessage(null), 5000);
      } else {
        setError(result.message || t('badges.applyFailed'));
      }
    } catch (err) {
      console.error('Failed to apply badge award:', err);
      
      // Extract detailed error message from API response
      let errorMessage = t('badges.applyFailedRetry');
      if (err.response?.data) {
        const errorData = err.response.data;
        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setApplyingBadge(false);
    }
  };

    const handleIconFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!ALLOWED_TYPES.includes(file.type)) {
      setError(t('badges.iconTypeError'));
      setIconFile(null);
      setIconPreview(null);
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setError(t('badges.iconSizeError'));
      setIconFile(null);
      setIconPreview(null);
      return;
    }

    setError(null);
    setIconFile(file);
    setIconRemoved(false); // Reset removal flag when new icon is selected

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setIconPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveIcon = () => {
    setIconFile(null);
    setIconPreview(null);
    setCurrentIconUrl(null);
    setIconRemoved(true);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setMinHoursError('');
      
      // Validate and convert Min Hours format if provided
      let minHoursDecimal = null;
      if (minHoursHHMM.trim() !== '') {
        const hhmmPattern = /^(\d+):([0-5][0-9])$/;
        if (!hhmmPattern.test(minHoursHHMM.trim())) {
          setMinHoursError(t('badges.minHoursInvalidFormat'));
          setSaving(false);
          return;
        }
        // Convert HH:MM to decimal hours
        minHoursDecimal = hhmmToDecimalHours(minHoursHHMM.trim());
        if (minHoursDecimal === null) {
          setMinHoursError(t('badges.minHoursInvalidShort'));
          setSaving(false);
          return;
        }
      }
      
      // Validate centres selection when badge is not global
      if (!formData.is_global && formData.applicable_centre_ids.length === 0) {
        setError(t('badges.selectCentreRequired'));
        setSaving(false);
        return;
      }
      
      const payload = {
        ...formData,
        min_hours: minHoursDecimal,
        applicable_centre_ids: formData.is_global 
          ? [] 
          : formData.applicable_centre_ids.map(id => Number(id)).filter(id => !isNaN(id))
      };

      // Handle icon: if new file selected, use it; if removed (had icon but now null), send null; otherwise don't include
      if (iconFile) {
        payload.icon = iconFile;
        setIconRemoved(false); // Reset removal flag if new icon is selected
      } else if (editingBadge && iconRemoved) {
        // User explicitly removed existing icon - send null to clear it
        payload.icon = null;
      }

      // #region agent log
      // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'BadgeConfigurationPage.js:handleSave',message:'Payload before update',data:{editingBadge:!!editingBadge,badgeId:editingBadge?.id,payload:JSON.stringify({...payload,icon:payload.icon?'[File]':null}),hasIcon:!!iconFile,isGlobal:payload.is_global,applicableTeamIds:payload.applicable_team_ids,minHours:payload.min_hours},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
      // #endregion

      if (editingBadge) {
        // #region agent log
        // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'BadgeConfigurationPage.js:handleSave',message:'Calling updateBadgeConfiguration',data:{badgeId:editingBadge.id},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
        // #endregion
        await updateBadgeConfiguration(editingBadge.id, payload);
        setSuccessMessage(t('badges.updatedSuccess'));
      } else {
        await createBadgeConfiguration(payload);
        setSuccessMessage(t('badges.createdSuccess'));
      }

      setShowEditModal(false);
      setEditingBadge(null);
      setIconFile(null);
      setIconPreview(null);
      setCurrentIconUrl(null);
      setIconRemoved(false);
      setMinHoursHHMM('');
      setMinHoursError('');
      setCentreSearchQuery('');
      await fetchBadges();

      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      // #region agent log
      // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'BadgeConfigurationPage.js:handleSave',message:'Error caught',data:{errorMessage:err.message,errorStack:err.stack,errorResponse:err.response?.data,status:err.response?.status},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
      // #endregion
      console.error('Failed to save badge:', err);
      
      // Extract detailed error message from API response
      let errorMessage = t('badges.saveFailed');
      if (err.response?.data) {
        const errorData = err.response.data;
        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (typeof errorData === 'object') {
          // Handle field-specific errors
          const fieldErrors = Object.entries(errorData)
            .map(([field, messages]) => {
              const fieldName = field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
              const msg = Array.isArray(messages) ? messages.join(', ') : String(messages);
              return `${fieldName}: ${msg}`;
            })
            .join('; ');
          if (fieldErrors) {
            errorMessage = fieldErrors;
          }
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setShowEditModal(false);
    setEditingBadge(null);
    setIconFile(null);
    setIconPreview(null);
    setCurrentIconUrl(null);
    setIconRemoved(false);
    setMinHoursHHMM('');
    setMinHoursError('');
    setCentreSearchQuery('');
    setFormData({
      name: '',
      description: '',
      points_value: 0,
      is_active: true,
      badge_type: 'DAILY',
      min_operations: 3,
      min_hours: null,
      tie_handling: 'no_badge',
      is_global: true,
      applicable_centre_ids: []
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };


  const tieHandlingLabel = (value) => {
    const map = {
      no_badge: t('badges.tieNoBadgeShort'),
      all_awarded: t('badges.tieAllAwarded'),
      random: t('badges.tieRandom')
    };
    return map[value] || value?.replace('_', ' ') || t('badges.tieNoBadgeShort');
  };

  const badgeTypeLabel = (type) => {
    const map = {
      DAILY: t('badges.typeDaily'),
      WEEKLY: t('badges.typeWeekly'),
      MONTHLY: t('badges.typeMonthly')
    };
    return map[type] || type || t('common:na');
  };

  const changeTypeLabel = (type) => {
    const map = {
      CREATED: t('badges.changeCreated'),
      UPDATED: t('badges.changeUpdated'),
      ACTIVATED: t('badges.changeActivated'),
      DEACTIVATED: t('badges.changeDeactivated')
    };
    return map[type] || type;
  };

  const formatDate = (dateString) => {
    if (!dateString) return t('common:na');
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(getDateLocale(), {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const labelForKey = (k) => (k || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  const formatInline = (value) => {
    if (value === null || value === undefined) return '—';
    if (typeof value === 'object') {
      if (Array.isArray(value)) return value.length ? value.map((v) => (typeof v === 'object' ? JSON.stringify(v) : String(v))).join(', ') : '—';
      return Object.keys(value).length ? JSON.stringify(value) : '—';
    }
    return String(value);
  };
  const getChangedPairs = (oldVal, newVal) => {
    const pairs = [];
    const oldObj = oldVal != null && typeof oldVal === 'object' && !Array.isArray(oldVal);
    const newObj = newVal != null && typeof newVal === 'object' && !Array.isArray(newVal);
    if (oldObj && newObj) {
      const keys = new Set([...Object.keys(oldVal), ...Object.keys(newVal)]);
      keys.forEach((k) => {
        const o = oldVal[k];
        const n = newVal[k];
        if (JSON.stringify(o) !== JSON.stringify(n)) pairs.push({ key: k, oldV: o, newV: n });
      });
    } else if (newObj && (oldVal == null || !oldObj)) {
      Object.entries(newVal).forEach(([k, v]) => pairs.push({ key: k, oldV: undefined, newV: v }));
    } else if (oldObj && (newVal == null || !newObj)) {
      Object.entries(oldVal).forEach(([k, v]) => pairs.push({ key: k, oldV: v, newV: undefined }));
    } else {
      pairs.push({ key: null, oldV: oldVal, newV: newVal });
    }
    return pairs;
  };

  // Convert decimal hours to HH:MM format
  const decimalHoursToHHMM = (decimalHours) => {
    // #region agent log
    // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'BadgeConfigurationPage.js:decimalHoursToHHMM',message:'Function called',data:{decimalHours:decimalHours,type:typeof decimalHours},timestamp:Date.now(),sessionId:'debug-session',runId:'run2',hypothesisId:'D'})}).catch(()=>{});
    // #endregion
    
    if (decimalHours === null || decimalHours === undefined || decimalHours === '') {
      // #region agent log
      // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'BadgeConfigurationPage.js:decimalHoursToHHMM',message:'Returning empty string (null/undefined)',data:{},timestamp:Date.now(),sessionId:'debug-session',runId:'run2',hypothesisId:'D'})}).catch(()=>{});
      // #endregion
      return '';
    }
    // Convert to number if it's a string
    const numHours = typeof decimalHours === 'string' ? parseFloat(decimalHours) : Number(decimalHours);
    if (isNaN(numHours) || numHours < 0) {
      // #region agent log
      // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'BadgeConfigurationPage.js:decimalHoursToHHMM',message:'Returning empty string (NaN or negative)',data:{numHours:numHours},timestamp:Date.now(),sessionId:'debug-session',runId:'run2',hypothesisId:'D'})}).catch(()=>{});
      // #endregion
      return '';
    }
    // Handle 0 case - should display as "0:00"
    if (numHours === 0) {
      const result = '0:00';
      // #region agent log
      // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'BadgeConfigurationPage.js:decimalHoursToHHMM',message:'Zero hours case',data:{result:result},timestamp:Date.now(),sessionId:'debug-session',runId:'run2',hypothesisId:'D'})}).catch(()=>{});
      // #endregion
      return result;
    }
    const hours = Math.floor(numHours);
    const minutes = Math.round((numHours % 1) * 60);
    const result = `${hours}:${minutes.toString().padStart(2, '0')}`;
    
    // #region agent log
    // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'BadgeConfigurationPage.js:decimalHoursToHHMM',message:'Conversion result',data:{numHours:numHours,hours:hours,minutes:minutes,result:result},timestamp:Date.now(),sessionId:'debug-session',runId:'run2',hypothesisId:'D'})}).catch(()=>{});
    // #endregion
    
    return result;
  };

  // Convert HH:MM format to decimal hours
  const hhmmToDecimalHours = (hhmm) => {
    if (!hhmm || hhmm.trim() === '') {
      return null;
    }
    // Validate format: digits:digits (e.g., 2:30, 100:45)
    const hhmmPattern = /^(\d+):([0-5][0-9])$/;
    const match = hhmm.trim().match(hhmmPattern);
    if (!match) {
      return null; // Invalid format
    }
    const hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    return hours + (minutes / 60);
  };

  // Format input to enforce HH:MM format as user types
  const formatMinHoursInput = (value) => {
    // Remove all non-digit and non-colon characters
    let cleaned = value.replace(/[^\d:]/g, '');
    
    // If empty, return empty
    if (cleaned === '') {
      return '';
    }
    
    // Find first colon position
    const colonIndex = cleaned.indexOf(':');
    
    // If no colon, just return the digits (hours)
    if (colonIndex === -1) {
      return cleaned;
    }
    
    // Split at first colon only (ignore additional colons)
    const hours = cleaned.substring(0, colonIndex);
    let minutes = cleaned.substring(colonIndex + 1).replace(/:/g, ''); // Remove any additional colons
    
    // Limit minutes to 2 digits
    if (minutes.length > 2) {
      minutes = minutes.substring(0, 2);
    }
    
    // Ensure minutes are valid (00-59) when complete
    if (minutes.length === 2) {
      const minNum = parseInt(minutes, 10);
      if (minNum > 59) {
        // Auto-correct to 59 if user tries to enter 60-99
        minutes = '59';
      }
    }
    
    // Return formatted string
    return `${hours}:${minutes}`;
  };


  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">{t('badges.title')}</h1>
          <p className="text-slate-600 mt-1">{t('badges.subtitle')}</p>
        </div>
        <button
          onClick={() => {
            setEditingBadge(null);
            setIconFile(null);
            setIconPreview(null);
            setCurrentIconUrl(null);
            setIconRemoved(false);
            setMinHoursHHMM('');
            setMinHoursError('');
            setCentreSearchQuery('');
            setFormData({
              name: '',
              description: '',
              points_value: 0,
              is_active: true,
              badge_type: 'DAILY',
              min_operations: 3,
              min_hours: null,
              tie_handling: 'no_badge',
              is_global: true,
              applicable_centre_ids: []
            });
            if (fileInputRef.current) {
              fileInputRef.current.value = '';
            }
            setShowEditModal(true);
          }}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
        >
          <span className="material-symbols-outlined text-[20px]">add</span>
          {t('badges.createBadge')}
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 dark:bg-red-900/30 dark:border-red-700/50 dark:text-red-200 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
          {successMessage}
        </div>
      )}

      {/* Badges List */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">{t('badges.colIcon')}</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">{t('badges.colBadge')}</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">{t('badges.colType')}</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">{t('badges.colPoints')}</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">{t('badges.colMinOps')}</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">{t('badges.colStatus')}</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">{t('badges.colScope')}</th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider">{t('badges.colActions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {badges.map((badge) => (
                <tr key={badge.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="w-12 h-12 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden">
                      {badge.icon ? (
                        <img 
                          src={getBadgeIconUrl(badge.icon)} 
                          alt={badge.name} 
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.src = defaultBadgeIcon;
                          }}
                        />
                      ) : (
                        <img src={defaultBadgeIcon} alt={t('badges.defaultIconAlt')} className="w-8 h-8 opacity-50" />
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <div className="font-semibold text-slate-900">{badge.name}</div>
                      {badge.description && (
                        <div className="text-sm text-slate-500 mt-1">{badge.description.substring(0, 50)}...</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600">{badgeTypeLabel(badge.badge_type)}</td>
                  <td className="px-6 py-4 text-sm font-semibold text-blue-600">{badge.points_value}</td>
                  <td className="px-6 py-4 text-sm text-slate-600">{badge.min_operations || 0}</td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
                      badge.is_active
                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-200 dark:border-emerald-700/70'
                        : 'bg-gray-50 text-gray-700 border border-gray-200 dark:bg-slate-700/60 dark:text-slate-200 dark:border-slate-600/80'
                    }`}>
                      <span className="material-symbols-outlined text-[14px]">
                        {badge.is_active ? 'check_circle' : 'cancel'}
                      </span>
                      {badge.is_active ? t('common:active') : t('common:inactive')}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600">
                    {badge.is_global ? t('badges.global') : t('badges.centresCount', { count: badge.applicable_centres?.length || 0 })}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleTestTrigger(badge)}
                        disabled={testingBadge === badge.id || !badge.is_active}
                        className="p-2 text-emerald-600 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-900/40 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title={badge.is_active ? t('badges.testBadgeAward') : t('badges.mustBeActiveToTest')}
                      >
                        <span className="material-symbols-outlined text-[20px]">
                          {testingBadge === badge.id ? 'hourglass_empty' : 'play_arrow'}
                        </span>
                      </button>
                      <button
                        onClick={() => handleEdit(badge)}
                        className="p-2 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/40 rounded-lg transition-colors"
                        title={t('common:edit')}
                      >
                        <span className="material-symbols-outlined text-[20px]">edit</span>
                      </button>
                      <button
                        onClick={() => handleViewHistory(badge)}
                        className="p-2 text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700 rounded-lg transition-colors"
                        title={t('badges.viewHistory')}
                      >
                        <span className="material-symbols-outlined text-[20px]">history</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {badges.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              {t('badges.emptyState')}
            </div>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      <AnimatePresence>
        {showEditModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6 border-b border-slate-200">
                <h2 className="text-2xl font-bold text-slate-900">
                  {editingBadge ? t('badges.editBadge') : t('badges.createBadge')}
                </h2>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('badges.badgeName')}</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 placeholder-slate-500 dark:placeholder-slate-400"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('common:description')}</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 placeholder-slate-500 dark:placeholder-slate-400"
                    rows="3"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t('badges.badgeIcon')}</label>
                  <div className="flex items-start gap-4">
                    {/* Icon Preview */}
                    <div className="flex-shrink-0">
                      <div className="w-20 h-20 rounded-lg border-2 border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden">
                        {iconPreview ? (
                          <img src={iconPreview} alt={t('badges.iconPreviewAlt')} className="w-full h-full object-cover" />
                        ) : currentIconUrl ? (
                          <img src={currentIconUrl} alt={t('badges.currentIconAlt')} className="w-full h-full object-cover" />
                        ) : (
                          <img src={defaultBadgeIcon} alt={t('badges.defaultIconAlt')} className="w-12 h-12 opacity-50" />
                        )}
                      </div>
                    </div>
                    {/* Upload Controls */}
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2">
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/jpeg,image/jpg,image/png"
                          onChange={handleIconFileSelect}
                          className="hidden"
                          id="badge-icon-upload"
                        />
                        <label
                          htmlFor="badge-icon-upload"
                          className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors cursor-pointer text-sm font-medium flex items-center gap-2"
                        >
                          <span className="material-symbols-outlined text-[18px]">upload</span>
                          <span>{t('badges.uploadIcon')}</span>
                        </label>
                        {(iconFile || currentIconUrl) && (
                          <button
                            type="button"
                            onClick={handleRemoveIcon}
                            className="px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 dark:bg-red-900/30 dark:text-red-200 dark:hover:bg-red-900/50 transition-colors text-sm font-medium flex items-center gap-2"
                          >
                            <span className="material-symbols-outlined text-[18px]">delete</span>
                            <span>{t('common:remove')}</span>
                          </button>
                        )}
                      </div>
                      <p className="text-xs text-slate-500">
                        {t('badges.iconHint')}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{t('badges.badgeType')}</label>
                    <select
                      value={formData.badge_type}
                      onChange={(e) => setFormData({ ...formData, badge_type: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 placeholder-slate-500 dark:placeholder-slate-400"
                    >
                      <option value="DAILY">{t('badges.typeDaily')}</option>
                      <option value="WEEKLY">{t('badges.typeWeekly')}</option>
                      <option value="MONTHLY">{t('badges.typeMonthly')}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{t('badges.pointsValue')}</label>
                    <input
                      type="number"
                      min="0"
                      value={formData.points_value}
                      onChange={(e) => setFormData({ ...formData, points_value: parseInt(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 placeholder-slate-500 dark:placeholder-slate-400"
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{t('badges.minOperations')}</label>
                    <input
                      type="number"
                      min="0"
                      value={formData.min_operations}
                      onChange={(e) => setFormData({ ...formData, min_operations: parseInt(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 placeholder-slate-500 dark:placeholder-slate-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      {t('badges.minHoursOptional')}
                      <span className="text-red-500 ml-1">*</span>
                      <span className="text-xs font-normal text-slate-500 ml-2">{t('badges.formatRequiredHint')}</span>
                    </label>
                    <div className={`flex rounded-lg border overflow-hidden focus-within:ring-2 ${
                      minHoursError
                        ? 'border-red-500 focus-within:ring-red-500 focus-within:border-red-500'
                        : 'border-slate-300 focus-within:ring-blue-500 focus-within:border-blue-500'
                    }`}>
                      <button
                        type="button"
                        onClick={() => {
                          const current = formData.min_hours != null ? Number(formData.min_hours) : 0;
                          const step = 15 / 60;
                          const next = Math.max(0, current - step);
                          const hhmm = next === 0 ? '' : decimalHoursToHHMM(next);
                          setMinHoursHHMM(hhmm);
                          setMinHoursError('');
                          setFormData({ ...formData, min_hours: next === 0 ? null : next });
                        }}
                        className="flex-shrink-0 w-10 flex items-center justify-center text-slate-600 hover:bg-slate-100 active:bg-slate-200 transition-colors border-r border-slate-300"
                        aria-label={t('badges.decreaseMinutes')}
                      >
                        <span className="material-symbols-outlined text-[20px]">remove</span>
                      </button>
                    <input
                      type="text"
                      pattern="^\d+:[0-5][0-9]$"
                      inputMode="numeric"
                      value={minHoursHHMM || ''}
                      // #region agent log
                      ref={(input) => {
                        if (input && showEditModal) {
                          const isVisible = input.offsetParent !== null;
                          const computedStyle = window.getComputedStyle(input);
                          // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'BadgeConfigurationPage.js:MinHoursInput',message:'Input field rendered and visibility check',data:{value:minHoursHHMM,editingBadge:!!editingBadge,showEditModal:showEditModal,isVisible:isVisible,display:computedStyle.display,visibility:computedStyle.visibility,opacity:computedStyle.opacity},timestamp:Date.now(),sessionId:'debug-session',runId:'run2',hypothesisId:'E'})}).catch(()=>{});
                        }
                      }}
                      // #endregion
                      onKeyDown={(e) => {
                        // Allow: backspace, delete, tab, escape, enter, colon, and numbers
                        const allowedKeys = [
                          'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
                          'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
                          'Home', 'End'
                        ];
                        
                        // Allow colon key
                        if (e.key === ':') {
                          // Only allow one colon
                          if (minHoursHHMM.includes(':')) {
                            e.preventDefault();
                          }
                          return;
                        }
                        
                        // Allow numbers
                        if (e.key >= '0' && e.key <= '9') {
                          return;
                        }
                        
                        // Allow control keys
                        if (allowedKeys.includes(e.key) || e.ctrlKey || e.metaKey) {
                          return;
                        }
                        
                        // Prevent all other keys
                        e.preventDefault();
                      }}
                      onPaste={(e) => {
                        e.preventDefault();
                        const pastedText = (e.clipboardData || window.clipboardData).getData('text');
                        // Format the pasted text
                        const formatted = formatMinHoursInput(pastedText);
                        setMinHoursHHMM(formatted);
                        
                        // Validate and update form data
                        if (formatted.trim() === '') {
                          setMinHoursError('');
                          setFormData({ ...formData, min_hours: null });
                        } else {
                          const hhmmPattern = /^(\d+):([0-5][0-9])$/;
                          if (hhmmPattern.test(formatted)) {
                            setMinHoursError('');
                            const decimalHours = hhmmToDecimalHours(formatted);
                            setFormData({ ...formData, min_hours: decimalHours });
                          } else {
                            if (formatted.includes(':')) {
                              const parts = formatted.split(':');
                              if (parts[1] && parseInt(parts[1], 10) > 59) {
                                setMinHoursError(t('badges.minHoursMinutesRange'));
                              } else if (parts[1]?.length !== 2) {
                                setMinHoursError(t('badges.minHoursMinutesDigits'));
                              } else {
                                setMinHoursError(t('badges.minHoursInvalidShort'));
                              }
                            } else {
                              setMinHoursError(t('badges.minHoursIncomplete'));
                            }
                          }
                        }
                      }}
                      onChange={(e) => {
                        let value = e.target.value;
                        
                        // Format the input to enforce HH:MM format
                        const formatted = formatMinHoursInput(value);
                        setMinHoursHHMM(formatted);
                        
                        // Validate format
                        if (formatted.trim() === '') {
                          setMinHoursError('');
                          setFormData({ ...formData, min_hours: null });
                        } else {
                          // Check if format is complete (has colon and 2-digit minutes)
                          const hhmmPattern = /^(\d+):([0-5][0-9])$/;
                          if (formatted.includes(':') && formatted.split(':')[1]?.length === 2) {
                            if (hhmmPattern.test(formatted)) {
                              setMinHoursError('');
                              // Convert HH:MM to decimal hours
                              const decimalHours = hhmmToDecimalHours(formatted);
                              setFormData({ ...formData, min_hours: decimalHours });
                            } else {
                              // Invalid minutes (e.g., 60-99)
                              const parts = formatted.split(':');
                              if (parts[1] && parseInt(parts[1], 10) > 59) {
                                setMinHoursError(t('badges.minHoursMinutesRange'));
                              } else {
                                setMinHoursError(t('badges.minHoursInvalidShort'));
                              }
                            }
                          } else if (formatted.includes(':')) {
                            // Has colon but minutes not complete yet - no error, just incomplete
                            setMinHoursError('');
                            setFormData({ ...formData, min_hours: null });
                          } else {
                            // No colon yet - no error, just incomplete
                            setMinHoursError('');
                            setFormData({ ...formData, min_hours: null });
                          }
                        }
                      }}
                      onBlur={(e) => {
                        const value = e.target.value.trim();
                        if (value) {
                          const hhmmPattern = /^(\d+):([0-5][0-9])$/;
                          if (!hhmmPattern.test(value)) {
                            if (!value.includes(':')) {
                              setMinHoursError(t('badges.minHoursIncomplete'));
                            } else {
                              const parts = value.split(':');
                              if (parts[1]?.length !== 2) {
                                setMinHoursError(t('badges.minHoursMinutesDigits'));
                              } else if (parseInt(parts[1], 10) > 59) {
                                setMinHoursError(t('badges.minHoursMinutesRange'));
                              } else {
                                setMinHoursError(t('badges.minHoursInvalidShort'));
                              }
                            }
                          } else {
                            setMinHoursError('');
                          }
                        } else {
                          setMinHoursError('');
                        }
                      }}
                      placeholder={t('badges.minHoursPlaceholder')}
                      className={`flex-1 min-w-0 px-3 py-2 border-0 bg-transparent font-mono tracking-wider outline-none ${
                        minHoursError ? 'focus:ring-0' : ''
                      }`}
                      style={{ fontFamily: 'monospace', letterSpacing: '0.05em' }}
                    />
                      <button
                        type="button"
                        onClick={() => {
                          const current = formData.min_hours != null ? Number(formData.min_hours) : 0;
                          const step = 15 / 60;
                          const next = current + step;
                          const hhmm = decimalHoursToHHMM(next);
                          setMinHoursHHMM(hhmm);
                          setMinHoursError('');
                          setFormData({ ...formData, min_hours: next });
                        }}
                        className="flex-shrink-0 w-10 flex items-center justify-center text-slate-600 hover:bg-slate-100 active:bg-slate-200 transition-colors border-l border-slate-300"
                        aria-label={t('badges.increaseMinutes')}
                      >
                        <span className="material-symbols-outlined text-[20px]">add</span>
                      </button>
                    </div>
                    {minHoursError ? (
                      <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                        <span className="material-symbols-outlined text-[16px]">error</span>
                        {minHoursError}
                      </p>
                    ) : (
                      <p className="text-xs text-slate-500 mt-1">
                        <span className="font-semibold">{t('badges.minHoursHelpPrefix')}</span> {t('badges.minHoursHelp')}
                      </p>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('badges.tieHandling')}</label>
                  <select
                    value={formData.tie_handling}
                    onChange={(e) => setFormData({ ...formData, tie_handling: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 placeholder-slate-500 dark:placeholder-slate-400"
                  >
                    <option value="no_badge">{t('badges.tieNoBadge')}</option>
                    <option value="all_awarded">{t('badges.tieAllAwarded')}</option>
                    <option value="random">{t('badges.tieRandom')}</option>
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="is_active" className="text-sm font-medium text-slate-700">{t('common:active')}</label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_global"
                    checked={formData.is_global}
                    onChange={(e) => setFormData({ ...formData, is_global: e.target.checked, applicable_centre_ids: e.target.checked ? [] : formData.applicable_centre_ids })}
                    className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="is_global" className="text-sm font-medium text-slate-700">{t('badges.globalBadge')}</label>
                </div>
                {!formData.is_global && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-slate-700">
                        {t('badges.applicableCentres')}
                        <span className="text-red-500 ml-1">*</span>
                      </label>
                      <div className="flex items-center gap-2 text-xs text-slate-600">
                        <span>{t('badges.selectedOf', { selected: selectedCentresCount, total: totalCentresCount })}</span>
                        {totalCentresCount > 0 && (
                          <>
                            <button
                              type="button"
                              onClick={handleSelectAllCentres}
                              className="text-blue-600 hover:text-blue-700 hover:underline"
                              disabled={selectedCentresCount === totalCentresCount}
                            >
                              {t('badges.selectAll')}
                            </button>
                            <span className="text-slate-300">|</span>
                            <button
                              type="button"
                              onClick={handleDeselectAllCentres}
                              className="text-blue-600 hover:text-blue-700 hover:underline"
                              disabled={selectedCentresCount === 0}
                            >
                              {t('badges.deselectAll')}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    
                    <div className="mb-3">
                      <input
                        type="text"
                        value={centreSearchQuery}
                        onChange={(e) => setCentreSearchQuery(e.target.value)}
                        placeholder={t('badges.searchCentres')}
                        className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 placeholder-slate-500 dark:placeholder-slate-400"
                      />
                    </div>

                    <div className="max-h-60 overflow-y-auto border border-slate-300 rounded-lg p-3 bg-slate-50">
                      {loadingCentres ? (
                        <div className="text-center py-4 text-sm text-slate-500">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2"></div>
                          {t('badges.loadingCentres')}
                        </div>
                      ) : filteredCentres.length === 0 ? (
                        <div className="text-center py-4 text-sm text-slate-500">
                          {centreSearchQuery ? t('badges.noCentresMatch') : t('badges.noCentresAvailable')}
                        </div>
                      ) : (
                        <div className="space-y-1">
                          {filteredCentres.map((centre) => {
                            const centreId = centre.id;
                            const isSelected = formData.applicable_centre_ids.some(id => 
                              String(id) === String(centreId) || Number(id) === Number(centreId)
                            );
                            return (
                              <div key={centreId} className="flex items-center gap-2 py-1.5 px-2 hover:bg-white rounded transition-colors">
                                <input
                                  type="checkbox"
                                  id={`centre-${centreId}`}
                                  checked={isSelected}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      if (!formData.applicable_centre_ids.some(id => 
                                        String(id) === String(centreId) || Number(id) === Number(centreId)
                                      )) {
                                        setFormData({ 
                                          ...formData, 
                                          applicable_centre_ids: [...formData.applicable_centre_ids, centreId] 
                                        });
                                      }
                                    } else {
                                      setFormData({ 
                                        ...formData, 
                                        applicable_centre_ids: formData.applicable_centre_ids.filter(id => 
                                          String(id) !== String(centreId) && Number(id) !== Number(centreId)
                                        ) 
                                      });
                                    }
                                  }}
                                  className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                                />
                                <label
                                  htmlFor={`centre-${centreId}`}
                                  className="text-sm text-slate-700 cursor-pointer flex-1"
                                >
                                  {centre.name || t('badges.centreFallback', { id: centreId })}
                                </label>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    
                    {!formData.is_global && formData.applicable_centre_ids.length === 0 && (
                      <p className="text-xs text-red-600 mt-2 flex items-center gap-1">
                        <span className="material-symbols-outlined text-[16px]">error</span>
                        {t('badges.atLeastOneCentre')}
                      </p>
                    )}
                  </div>
                )}
              </div>
              <div className="p-6 border-t border-slate-200 flex items-center justify-end gap-3">
                <button
                  onClick={handleCancel}
                  className="px-4 py-2 text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
                  disabled={saving}
                >
                  {t('common:cancel')}
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving || !formData.name || minHoursError !== '' || (!formData.is_global && formData.applicable_centre_ids.length === 0)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  title={
                    minHoursError 
                      ? t('badges.fixMinHoursBeforeSave') 
                      : (!formData.is_global && formData.applicable_centre_ids.length === 0)
                      ? t('badges.selectCentreBeforeSave')
                      : ''
                  }
                >
                  <span className="material-symbols-outlined text-[18px]">save</span>
                  <span>{t('common:save')}</span>
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* History Modal */}
      <AnimatePresence>
        {showHistoryModal && editingBadge && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6 border-b border-slate-200">
                <h2 className="text-2xl font-bold text-slate-900">{t('badges.changeHistory', { name: editingBadge.name })}</h2>
              </div>
              <div className="p-6">
                {loadingHistory ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  </div>
                ) : history.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">{t('badges.noChangeHistory')}</div>
                ) : (
                  <div className="space-y-4">
                    {history.map((entry) => (
                      <div key={entry.id} className="border border-slate-200 rounded-lg p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <div className="font-semibold text-slate-900">
                              {changeTypeLabel(entry.change_type)}
                            </div>
                            {entry.field_name && (
                              <div className="text-sm text-slate-600 mt-1">{t('badges.fieldLabel', { name: entry.field_name })}</div>
                            )}
                          </div>
                          <div className="text-sm text-slate-500">{formatDate(entry.changed_at)}</div>
                        </div>
                        {entry.changed_by_name && (
                          <div className="text-sm text-slate-600 mb-2">{t('badges.changedBy', { name: entry.changed_by_name })}</div>
                        )}
                        {entry.change_type !== 'CREATED' && (entry.old_value_display != null || entry.new_value_display != null) && (() => {
                          const pairs = getChangedPairs(entry.old_value_display, entry.new_value_display);
                          if (pairs.length === 0) return null;
                          return (
                            <div className="space-y-2 mt-3 text-sm">
                              {pairs.map(({ key, oldV, newV }) => {
                                const isMinHours = key === 'min_hours' || entry.field_name === 'min_hours';
                                const fmt = (v) => (isMinHours ? (decimalHoursToHHMM(v) || formatInline(v)) : formatInline(v));
                                return (
                                <div key={key ?? 'value'} className="flex flex-wrap items-baseline gap-1">
                                  <span className="font-medium text-slate-700">
                                    {(key != null ? labelForKey(key) : (entry.field_name ? labelForKey(entry.field_name) : t('badges.valueLabel')))}:
                                  </span>
                                  <span className="text-slate-600 bg-slate-50 px-1.5 py-0.5 rounded">{fmt(oldV)}</span>
                                  <span className="text-slate-400">→</span>
                                  <span className="text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded">{fmt(newV)}</span>
                                </div>
                              ); })}
                            </div>
                          );
                        })()}
                        {entry.change_reason && (
                          <div className="mt-2 text-sm text-slate-600 italic">{t('badges.reasonPrefix', { reason: entry.change_reason })}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="p-6 border-t border-slate-200">
                <button
                  onClick={() => {
                    setShowHistoryModal(false);
                    setEditingBadge(null);
                  }}
                  className="w-full px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
                >
                  {t('common:close')}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Test Results Modal */}
      <AnimatePresence>
        {showTestResultsModal && testResults && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900">
                      {t('badges.testResultsTitle', { name: testResults.badge.name })}
                    </h2>
                    <p className="text-sm text-slate-600 mt-1">
                      {testResults.evaluationOnly 
                        ? t('badges.evaluationSummary', { count: testResults.wouldAwardTo || 0 })
                        : testResults.result.success 
                          ? t('badges.awardedSummary', { count: testResults.result.awarded_employees?.length || 0 })
                          : t('badges.testCompleted')}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setShowTestResultsModal(false);
                      setTestResults(null);
                    }}
                    className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    <span className="material-symbols-outlined text-[24px]">close</span>
                  </button>
                </div>
              </div>
              <div className="p-6">
                {testResults.allEvaluatedEmployees.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <span className="material-symbols-outlined text-[48px] text-slate-300 mb-4">people</span>
                    <p>{t('badges.noEmployeesFound')}</p>
                    <p className="text-sm mt-2">{t('badges.noEmployeesHint')}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Badge Criteria */}
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                        <span className="material-symbols-outlined text-[18px]">info</span>
                        {t('badges.badgeCriteria')}
                      </h3>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <div className="text-slate-600 font-medium">{t('badges.minOperations')}</div>
                          <div className="text-lg font-bold text-slate-900">
                            {testResults.badgeCriteria.min_operations || 0}
                          </div>
                        </div>
                        <div>
                          <div className="text-slate-600 font-medium">{t('badges.minHours')}</div>
                          <div className="text-lg font-bold text-slate-900">
                            {testResults.badgeCriteria.min_hours != null && testResults.badgeCriteria.min_hours !== ''
                              ? decimalHoursToHHMM(testResults.badgeCriteria.min_hours) || '—'
                              : t('common:none')}
                          </div>
                        </div>
                        <div>
                          <div className="text-slate-600 font-medium">{t('badges.tieHandling')}</div>
                          <div className="text-lg font-bold text-slate-900 capitalize">
                            {tieHandlingLabel(testResults.badgeCriteria.tie_handling)}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Why Winner Won */}
                    {testResults.awardedEmployeeIds.length > 0 && (
                      <div className="bg-emerald-50 border border-emerald-200 dark:bg-emerald-900/30 dark:border-emerald-700/50 rounded-lg p-4">
                        <h3 className="text-sm font-semibold text-emerald-700 mb-2 flex items-center gap-2">
                          <span className="material-symbols-outlined text-[18px]">emoji_events</span>
                          {testResults.evaluationOnly ? t('badges.whoWouldWin') : t('badges.whyWinnersWon')}
                        </h3>
                        <p className="text-sm text-emerald-800">
                          {testResults.evaluationOnly ? (
                            <>
                              {t('badges.wouldWinExplanation', { maxOps: testResults.maxOperationCount })}
                              {testResults.badgeCriteria.min_operations > 0 && t('badges.meetingMinOps', { count: testResults.badgeCriteria.min_operations })}
                              {testResults.badgeCriteria.min_hours != null && testResults.badgeCriteria.min_hours !== '' && t('badges.andMinHours', { hours: decimalHoursToHHMM(testResults.badgeCriteria.min_hours) })}.
                              {testResults.awardedEmployeeIds.length > 1 && (
                                <span> {t('badges.multipleWinnersWould', { rule: tieHandlingLabel(testResults.badgeCriteria.tie_handling) })}</span>
                              )}
                            </>
                          ) : (
                            <>
                              {t('badges.wonExplanation', { maxOps: testResults.maxOperationCount })}
                              {testResults.badgeCriteria.min_operations > 0 && t('badges.meetingMinOps', { count: testResults.badgeCriteria.min_operations })}
                              {testResults.badgeCriteria.min_hours != null && testResults.badgeCriteria.min_hours !== '' && t('badges.andMinHours', { hours: decimalHoursToHHMM(testResults.badgeCriteria.min_hours) })}.
                              {testResults.awardedEmployeeIds.length > 1 && (
                                <span> {t('badges.multipleWinnersWere', { rule: tieHandlingLabel(testResults.badgeCriteria.tie_handling) })}</span>
                              )}
                            </>
                          )}
                        </p>
                      </div>
                    )}

                    {/* Why No One Won */}
                    {testResults.awardedEmployeeIds.length === 0 && testResults.allEvaluatedEmployees.length > 0 && (
                      <div className="bg-amber-50 border border-amber-200 dark:bg-amber-900/30 dark:border-amber-700/50 rounded-lg p-4">
                        <h3 className="text-sm font-semibold text-amber-700 mb-2 flex items-center gap-2">
                          <span className="material-symbols-outlined text-[18px]">info</span>
                          {t('badges.whyNoWinner')}
                        </h3>
                        <div className="space-y-2 text-sm text-amber-800">
                          {(() => {
                            const eligibleEmployees = testResults.allEvaluatedEmployees.filter(e => e.is_eligible);
                            const hasEligible = eligibleEmployees.length > 0;
                            
                            if (!hasEligible) {
                              // No eligible employees
                              return (
                                <div>
                                  <p className="font-medium mb-1">{t('badges.noEmployeesMetReqs')}</p>
                                  <ul className="list-disc list-inside ml-2 space-y-1">
                                    {testResults.badgeCriteria.min_operations > 0 && (
                                      <li>
                                        {t('badges.minOpsNoReach', { count: testResults.badgeCriteria.min_operations })}
                                        {testResults.maxOperationCount > 0 && (
                                          <span>{t('badges.highestWasOps', { count: testResults.maxOperationCount })}</span>
                                        )}
                                      </li>
                                    )}
                                    {testResults.badgeCriteria.min_hours != null && testResults.badgeCriteria.min_hours !== '' && (
                                      <li>
                                        {t('badges.minHoursNoReach', { hours: decimalHoursToHHMM(testResults.badgeCriteria.min_hours) })}
                                        {(() => {
                                          const maxHours = Math.max(...testResults.allEvaluatedEmployees.map(e => e.total_hours), 0);
                                          return maxHours > 0 ? t('badges.highestWasHours', { hours: decimalHoursToHHMM(maxHours) }) : '';
                                        })()}
                                      </li>
                                    )}
                                  </ul>
                                </div>
                              );
                            } else {
                              // Has eligible employees but no winner
                              const sortedEligible = [...eligibleEmployees].sort((a, b) => b.operation_count - a.operation_count);
                              const topCount = sortedEligible.length > 0 ? sortedEligible[0].operation_count : 0;
                              const topPerformers = sortedEligible.filter(e => e.operation_count === topCount);
                              const isTie = topPerformers.length > 1;
                              
                              return (
                                <div>
                                  <p className="font-medium mb-1">{t('badges.eligibleButNoAward')}</p>
                                  <ul className="list-disc list-inside ml-2 space-y-1">
                                    {isTie && testResults.badgeCriteria.tie_handling === 'no_badge' && (
                                      <li>
                                        {t('badges.tieHandlingRule', { count: topPerformers.length, ops: topCount })}
                                      </li>
                                    )}
                                    {!isTie && topCount === 0 && (
                                      <li>
                                        {t('badges.noOperations')}
                                      </li>
                                    )}
                                    {!isTie && topCount > 0 && testResults.badgeCriteria.tie_handling === 'no_badge' && (
                                      <li>
                                        {t('badges.configIssue', { count: topCount })}
                                      </li>
                                    )}
                                  </ul>
                                </div>
                              );
                            }
                          })()}
                          <div className="mt-3 pt-3 border-t border-amber-300">
                            <p className="text-xs text-amber-700">
                              {t('badges.tipReviewList')}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Summary */}
                    <div className="bg-blue-50 border border-blue-200 dark:bg-blue-900/30 dark:border-blue-700/50 rounded-lg p-4">
                      <div className="grid grid-cols-4 gap-4 text-sm">
                        <div>
                          <div className="text-blue-600 font-medium">{t('badges.totalCandidates')}</div>
                          <div className="text-2xl font-bold text-blue-900">{testResults.allEvaluatedEmployees.length}</div>
                        </div>
                        <div>
                          <div className="text-blue-600 font-medium">{t('badges.eligible')}</div>
                          <div className="text-2xl font-bold text-blue-700">
                            {testResults.allEvaluatedEmployees.filter(e => e.is_eligible).length}
                          </div>
                        </div>
                        <div>
                          <div className="text-blue-600 font-medium">{t('badges.winners')}</div>
                          <div className="text-2xl font-bold text-emerald-600">{testResults.awardedEmployeeIds.length}</div>
                        </div>
                        <div>
                          <div className="text-blue-600 font-medium">{t('badges.maxOperations')}</div>
                          <div className="text-2xl font-bold text-blue-900">{testResults.maxOperationCount}</div>
                        </div>
                      </div>
                    </div>

                    {/* Employees List */}
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-slate-50 border-b border-slate-200">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">{t('badges.colStatus')}</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">{t('badges.colEmployee')}</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">{t('badges.colTeam')}</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">{t('badges.colOperations')}</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">{t('badges.colHours')}</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">{t('badges.colWhyNotWinner')}</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200">
                          {testResults.allEvaluatedEmployees
                            .sort((a, b) => {
                              // Sort by: winners first, then by operation count descending
                              const aIsWinner = testResults.awardedEmployeeIds.includes(a.employee_id);
                              const bIsWinner = testResults.awardedEmployeeIds.includes(b.employee_id);
                              if (aIsWinner && !bIsWinner) return -1;
                              if (!aIsWinner && bIsWinner) return 1;
                              return b.operation_count - a.operation_count;
                            })
                            .map((employee) => {
                              const isWinner = testResults.awardedEmployeeIds.includes(employee.employee_id);
                              const gapToWin = testResults.maxOperationCount - employee.operation_count;
                              const isClose = !isWinner && employee.is_eligible && gapToWin <= 2 && gapToWin > 0;
                              const isNotEligible = !employee.is_eligible;
                              
                              return (
                                <tr 
                                  key={employee.employee_id} 
                                  className={`transition-colors ${
                                    isWinner 
                                      ? 'bg-emerald-50 hover:bg-emerald-100' 
                                      : isClose
                                      ? 'bg-amber-50 hover:bg-amber-100'
                                      : isNotEligible
                                      ? 'bg-red-50 hover:bg-red-100'
                                      : 'hover:bg-slate-50'
                                  }`}
                                >
                                  <td className="px-4 py-3">
                                    {isWinner ? (
                                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-200 dark:border-emerald-700/70">
                                        <span className="material-symbols-outlined text-[14px]">emoji_events</span>
                                        {t('badges.winner')}
                                      </span>
                                    ) : isClose ? (
                                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700 border border-amber-200 dark:bg-amber-900/40 dark:text-amber-200 dark:border-amber-700/70">
                                        <span className="material-symbols-outlined text-[14px]">trending_up</span>
                                        {t('badges.close')}
                                      </span>
                                    ) : isNotEligible ? (
                                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700 border border-red-200 dark:bg-red-900/40 dark:text-red-200 dark:border-red-700/70">
                                        <span className="material-symbols-outlined text-[14px]">cancel</span>
                                        {t('badges.notEligible')}
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600 border border-slate-200 dark:bg-slate-700/60 dark:text-slate-200 dark:border-slate-600/80">
                                        <span className="material-symbols-outlined text-[14px]">person</span>
                                        {t('badges.eligible')}
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-4 py-3">
                                    <div>
                                      <div className="font-medium text-slate-900">{employee.employee_name}</div>
                                      <div className="text-xs text-slate-500">{employee.employee_email}</div>
                                    </div>
                                  </td>
                                  <td className="px-4 py-3 text-sm text-slate-600">{employee.team_name}</td>
                                  <td className="px-4 py-3">
                                    <div className="flex items-center gap-2">
                                      <span className={`font-semibold ${isWinner ? 'text-emerald-700' : 'text-slate-900'}`}>
                                        {employee.operation_count}
                                      </span>
                                      {testResults.badgeCriteria.min_operations > 0 && (
                                        <span 
                                          className={`text-xs ${employee.meets_min_operations ? 'text-emerald-600' : 'text-red-600'}`}
                                          title={employee.meets_min_operations 
                                            ? t('badges.meetsMinReq', { value: testResults.badgeCriteria.min_operations })
                                            : t('badges.doesNotMeetMinReq', { value: testResults.badgeCriteria.min_operations })}
                                        >
                                          <span className="material-symbols-outlined text-[14px]">
                                            {employee.meets_min_operations ? 'check_circle' : 'cancel'}
                                          </span>
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-4 py-3 text-sm text-slate-600">
                                    <div className="flex items-center gap-2">
                                      <span>{decimalHoursToHHMM(employee.total_hours) || employee.total_hours}</span>
                                      {testResults.badgeCriteria.min_hours != null && testResults.badgeCriteria.min_hours !== '' && (
                                        <span 
                                          className={`text-xs ${employee.meets_min_hours ? 'text-emerald-600' : 'text-red-600'}`}
                                          title={employee.meets_min_hours 
                                            ? t('badges.meetsMinReq', { value: decimalHoursToHHMM(testResults.badgeCriteria.min_hours) })
                                            : t('badges.doesNotMeetMinReq', { value: decimalHoursToHHMM(testResults.badgeCriteria.min_hours) })}
                                        >
                                          <span className="material-symbols-outlined text-[14px]">
                                            {employee.meets_min_hours ? 'check_circle' : 'cancel'}
                                          </span>
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-4 py-3">
                                    {isWinner ? (
                                      <span className="text-sm font-medium text-emerald-600">{t('badges.wonHighestOps')}</span>
                                    ) : !employee.is_eligible ? (
                                      <span className="text-sm text-red-600">
                                        {!employee.meets_min_operations && testResults.badgeCriteria.min_operations > 0 && (
                                          <span>{t('badges.needsMoreOps', { count: testResults.badgeCriteria.min_operations - employee.operation_count })}</span>
                                        )}
                                        {!employee.meets_min_operations && !employee.meets_min_hours && testResults.badgeCriteria.min_hours != null && testResults.badgeCriteria.min_hours !== '' && ' • '}
                                        {!employee.meets_min_hours && testResults.badgeCriteria.min_hours != null && testResults.badgeCriteria.min_hours !== '' && (
                                          <span>{t('badges.needsMoreHours', { hours: decimalHoursToHHMM(Math.max(0, testResults.badgeCriteria.min_hours - employee.total_hours)) })}</span>
                                        )}
                                      </span>
                                    ) : gapToWin > 0 ? (
                                      <span className="text-sm text-slate-600">
                                        {t('badges.opsAway', { count: gapToWin })}
                                      </span>
                                    ) : (
                                      <span className="text-sm text-slate-400">—</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
              <div className="p-6 border-t border-slate-200 flex items-center justify-between gap-3">
                <button
                  onClick={() => {
                    setShowTestResultsModal(false);
                    setTestResults(null);
                    setApplyingBadge(false);
                  }}
                  className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
                >
                  {t('common:close')}
                </button>
                {testResults.evaluationOnly && testResults.wouldAwardTo > 0 && (
                  <button
                    onClick={handleApplyBadge}
                    disabled={applyingBadge}
                    className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {applyingBadge ? (
                      <>
                        <span className="material-symbols-outlined text-[18px] animate-spin">hourglass_empty</span>
                        <span>{t('badges.applying')}</span>
                      </>
                    ) : (
                      <>
                        <span className="material-symbols-outlined text-[18px]">check_circle</span>
                        <span>{t('badges.applyBadgeTo', { count: testResults.wouldAwardTo })}</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BadgeConfigurationPage;

